/**
 * The Review queue's comparisons, shared by the server and the screen.
 *
 * Browser-safe: types and arithmetic only (see `index.ts`).
 *
 * - `matchPoLine`: which purchase-order line an invoice line is billing for.
 *   Moved here from `apps/api/src/read/review-queue.ts` so the review screen can
 *   compare an AI proposal with a purchase order BEFORE anything is saved, with
 *   the same rule the server uses for saved lines.
 * - `buildAiComparisonRows`: the fact rows of the AI review mode
 *   (docs/plans/trbud-receipt-review-modes.md, 3.6 as amended by 8.2 M7 and
 *   8.8): what the paper says beside what TruckBuddy has, with a verdict.
 * - `queueTabFor`: which tab of the queue an item belongs to (8.7). The server
 *   filters in SQL by the same rule; a dbtest holds the two together.
 */
import { dateOutOfWindow } from "./field-shapes.js";
import { amountsEqual, negateAmount, normaliseAmount, sumAmounts } from "./money.js";
import { isLowConfidence, type AiProposal } from "./receipt-parser.js";
import type { InboxItem, ReviewDetail } from "./wire.js";

/* ── purchase-order line matching ──────────────────────────────────── */

/** An invoice line, as far as matching needs it. */
export interface PoMatchLine {
  /** The catalogue part the line was matched to, if any. An AI line has none. */
  matchedPartId: string | null;
  description: string;
}

/** A live purchase-order line, as far as matching needs it. */
export interface PoMatchCandidate {
  partId: string | null;
  /** The order's own words for the line, or null for a part-only line. */
  description: string | null;
}

/**
 * Which purchase-order line, if any, this invoice line is billing for.
 *
 * Part id first and description second, and the ORDER is the whole content of
 * this function. A catalogue part is an identity both sides agree on; a
 * description is a guess, because a vendor writes "Air filter, Donaldson P182039"
 * where the order said "air filter". So the part match is authoritative when it
 * exists and the text match is a fallback that SAYS it is one: `matchedOn`
 * travels to the screen so a reviewer can see which kind of match they trust.
 *
 * Deliberately NOT fuzzy. `v_vendor_match_queue` shipped with two of four match
 * signals dead because a regex was copied between a template literal and a
 * `.sql` file, and the lesson recorded from it is that a similarity score nobody
 * can explain is worse than a containment test everybody can. Containment in
 * either direction, case-folded, and nothing else.
 */
export function matchPoLine<T extends PoMatchCandidate>(
  line: PoMatchLine,
  poLines: readonly T[],
): { line: T; matchedOn: "part" | "description" } | null {
  if (line.matchedPartId !== null) {
    const byPart = poLines.find((po) => po.partId === line.matchedPartId);
    if (byPart) return { line: byPart, matchedOn: "part" };
  }
  const wanted = line.description.trim().toLowerCase();
  if (wanted.length === 0) return null;
  const byText = poLines.find((po) => {
    const ordered = (po.description ?? "").trim().toLowerCase();
    if (ordered.length === 0) return false;
    return ordered.includes(wanted) || wanted.includes(ordered);
  });
  return byText ? { line: byText, matchedOn: "description" } : null;
}

/* ── the AI review mode's comparison rows ──────────────────────────── */

/**
 * - `match`: the paper agrees with the record.
 * - `check`: it does not, or a worker check fired. Look before accepting.
 * - `note`: it differs in a way that is often innocent (a dealer's own stock
 *   number printed as UNIT#). Said, not flagged.
 * - `nothing`: there is nothing to compare it with.
 */
export type AiComparisonVerdict = "match" | "check" | "note" | "nothing";

export interface AiComparisonRow {
  /** `vendor`, `invoice`, `work_order`, `purchase_order`, `total`, or `check:<name>`. */
  key: string;
  label: string;
  /** What the AI read off the paper, in words. */
  paper: string | null;
  /** What TruckBuddy has to compare it with, in words. */
  record: string | null;
  verdict: AiComparisonVerdict;
  /** A sentence explaining the verdict, or null. */
  message: string | null;
  /** The AI's score for the value, when it has one. */
  confidence: number | null;
  /** A `check` verdict, or a score under the proposal's own threshold. */
  flagged: boolean;
}

/** One purchase-order line as the comparison takes it (the wire's `ReviewDetail.purchaseOrder.lines`). */
export interface ComparisonPoLine {
  lineNumber: number;
  partId: string | null;
  description: string;
  quantityOrdered: string;
  unitCost: string;
}

export interface AiComparisonOptions {
  /** For the invoice date's window. Defaults to now; a test passes a fixed instant. */
  now?: Date;
  timeZone?: string;
}

/** The checks with a row of their own; every other check becomes a row by itself. */
const CHECKS_WITH_A_ROW = new Set(["duplicate_invoice", "arithmetic"]);

const CHECK_LABELS: Record<string, string> = {
  ces_vendor: "CES as vendor",
  payment_number_scrubbed: "Card number",
  handwritten: "Handwriting",
  sensitive_payment_data: "Payment details",
  other_documents: "Other bills in the file",
};

/** Case, spaces and dashes ignored: `E-2116-lrd` and `E2116LRD` are one unit. */
function unitKey(value: string): string {
  return value.toLowerCase().replace(/[\s-]+/g, "");
}

function vinKey(value: string): string {
  return value.toUpperCase().replace(/\s+/g, "");
}

function four(value: string): number {
  return Math.round(Number.parseFloat(value) * 10000) / 10000;
}

/**
 * The fact rows of the AI review mode, in a fixed order: Vendor, Invoice
 * no./date, Work order, Purchase order, Total, then every worker check that has
 * no row of its own. One row per fact, never filtered, so the screen and a test
 * see the same list.
 *
 * `purchaseOrderLines` is the order the person has PICKED in the pane (or the
 * one already linked), or null for none: the row then says there is nothing to
 * compare.
 */
export function buildAiComparisonRows(
  proposal: AiProposal,
  detail: Pick<ReviewDetail, "workOrderId" | "workOrderDisplayId" | "unitLabel" | "vin">,
  purchaseOrderLines: readonly ComparisonPoLine[] | null,
  options: AiComparisonOptions = {},
): AiComparisonRow[] {
  const threshold = proposal.threshold;
  const checks = proposal.checks;
  const checkOf = (name: string) => checks.find((c) => c.check === name) ?? null;
  const row = (
    r: Omit<AiComparisonRow, "flagged">,
  ): AiComparisonRow => ({ ...r, flagged: r.verdict === "check" || isLowConfidence(r.confidence, threshold) });

  const rows: AiComparisonRow[] = [];

  // Vendor: an exact (case-insensitive) name match against the vendor list,
  // which the server has already looked up as `vendorId`.
  const vendorName = proposal.vendorName.value;
  rows.push(
    row({
      key: "vendor",
      label: "Vendor",
      paper: vendorName,
      record: proposal.vendorId !== null ? vendorName : null,
      verdict: vendorName === null ? "nothing" : proposal.vendorId !== null ? "match" : "check",
      message:
        vendorName === null
          ? "The AI found no vendor name."
          : proposal.vendorId !== null
            ? "A vendor on file has this name."
            : "New vendor, or the name differs from the one on file.",
      confidence: proposal.vendorName.confidence,
    }),
  );

  // Invoice number and date: the duplicate check, and the entry form's window.
  const number = proposal.invoiceNumber.value;
  const date = proposal.invoiceDate.value;
  const duplicate = checkOf("duplicate_invoice");
  const dateProblem =
    date !== null ? dateOutOfWindow(date, "recorded", options.now ?? new Date(), options.timeZone) : null;
  const invoicePaper = [number, date].filter((v): v is string => v !== null).join(" · ") || null;
  rows.push(
    row({
      key: "invoice",
      label: "Invoice no. / date",
      paper: invoicePaper,
      record: null,
      verdict: duplicate || dateProblem ? "check" : invoicePaper === null ? "nothing" : "match",
      message: duplicate?.message ?? dateProblem ?? (invoicePaper === null ? "The AI found no invoice number or date." : null),
      confidence: proposal.invoiceNumber.confidence,
    }),
  );

  rows.push(row(workOrderRow(proposal, detail)));
  rows.push(row(purchaseOrderRow(proposal, purchaseOrderLines)));

  // Total: the lines, signed by kind, against the total printed.
  const signed = proposal.lines
    .filter((line) => line.amount !== null)
    .map((line) => (line.kind === "DISCOUNT" ? negateAmount(line.amount!) : line.amount!));
  const linesTotal = sumAmounts(signed);
  const stated = proposal.totalAmount.value;
  const arithmetic = checkOf("arithmetic");
  const totalsAgree = stated !== null && amountsEqual(linesTotal, stated);
  rows.push(
    row({
      key: "total",
      label: "Total",
      paper: stated !== null ? normaliseAmount(stated) : null,
      record: linesTotal,
      verdict: stated === null ? "nothing" : totalsAgree && !arithmetic ? "match" : "check",
      message:
        stated === null
          ? "The AI found no total on the paper."
          : !totalsAgree
            ? `The lines add to ${linesTotal}; the paper says ${normaliseAmount(stated)}.`
            : (arithmetic?.message ?? null),
      confidence: proposal.totalAmount.confidence,
    }),
  );

  for (const check of checks) {
    if (CHECKS_WITH_A_ROW.has(check.check)) continue;
    rows.push(
      row({
        key: `check:${check.check}`,
        label: CHECK_LABELS[check.check] ?? "Check",
        paper: null,
        record: null,
        verdict: "check",
        message: check.message,
        confidence: null,
      }),
    );
  }
  return rows;
}

/**
 * The work-order row (8.8). The record side is the work order's asset; the paper
 * side is the units the receipt names. VIN first, then unit, which is the order
 * the extractor prompt uses: CES unit numbers are reused over time, so a unit
 * match alone is weaker evidence than a VIN.
 */
function workOrderRow(
  proposal: AiProposal,
  detail: Pick<ReviewDetail, "workOrderId" | "workOrderDisplayId" | "unitLabel" | "vin">,
): Omit<AiComparisonRow, "flagged"> {
  const base = { key: "work_order", label: "Work order" };
  const vins = proposal.assets.map((a) => a.vin).filter((v): v is string => v !== null && v.trim() !== "");
  const units = proposal.assets
    .map((a) => a.unitNumber)
    .filter((u): u is string => u !== null && u.trim() !== "");
  const confidence = proposal.assets.find((a) => a.vin !== null || a.unitNumber !== null)?.confidence ?? null;
  const record =
    [detail.workOrderDisplayId, detail.unitLabel, detail.vin ? `VIN ${detail.vin}` : null]
      .filter((v): v is string => v !== null)
      .join(" · ") || null;
  const paper =
    [units.length > 0 ? `Unit ${units.join(", ")}` : null, vins.length > 0 ? `VIN ${vins.join(", ")}` : null]
      .filter((v): v is string => v !== null)
      .join(" · ") || null;

  if (detail.workOrderId === null) {
    return { ...base, paper, record: null, verdict: "nothing", confidence, message: "This file is not on a work order." };
  }
  if (vins.length > 0 && detail.vin) {
    const matched = vins.some((v) => vinKey(v) === vinKey(detail.vin!));
    return {
      ...base,
      paper,
      record,
      verdict: matched ? "match" : "check",
      confidence,
      message: matched
        ? "The VIN on the receipt is this work order's asset."
        : `The receipt's VIN (${vins.join(", ")}) is not this asset's VIN (${detail.vin}).`,
    };
  }
  if (units.length > 0 && detail.unitLabel) {
    const matched = units.some((u) => unitKey(u) === unitKey(detail.unitLabel!));
    return {
      ...base,
      paper,
      record,
      verdict: matched ? "match" : "note",
      confidence,
      message: matched
        ? "The unit on the receipt is this work order's asset."
        : `The receipt says unit ${units.join(", ")}, the work order is for ${detail.unitLabel}. ` +
          "A dealer often prints its own stock number as UNIT#, so this may be fine.",
    };
  }
  return {
    ...base,
    paper,
    record,
    verdict: "nothing",
    confidence,
    message: paper === null ? "The receipt names no unit or VIN." : "There is no unit or VIN on record to compare.",
  };
}

function purchaseOrderRow(
  proposal: AiProposal,
  poLines: readonly ComparisonPoLine[] | null,
): Omit<AiComparisonRow, "flagged"> {
  const base = { key: "purchase_order", label: "Purchase order", confidence: null };
  if (poLines === null) {
    return { ...base, paper: null, record: null, verdict: "nothing", message: "No purchase order is picked." };
  }
  const problems: string[] = [];
  let compared = 0;
  for (const line of proposal.lines) {
    if (line.kind === "TAX" || line.kind === "DISCOUNT") continue;
    compared += 1;
    const matched = matchPoLine({ matchedPartId: null, description: line.description }, poLines);
    if (!matched) {
      problems.push(`Line ${line.lineNumber} (${line.description}) is not on the order.`);
      continue;
    }
    if (line.unitCost !== null && four(line.unitCost) !== four(matched.line.unitCost)) {
      problems.push(
        `Line ${line.lineNumber} is billed at ${line.unitCost}; the order says ${matched.line.unitCost}.`,
      );
    }
  }
  const verdict: AiComparisonVerdict = compared === 0 ? "nothing" : problems.length > 0 ? "check" : "match";
  return {
    ...base,
    paper: `${proposal.lines.length} line${proposal.lines.length === 1 ? "" : "s"}`,
    record: `${poLines.length} ordered line${poLines.length === 1 ? "" : "s"}`,
    verdict,
    message:
      verdict === "nothing"
        ? "No priced line to compare with the order."
        : problems.length > 0
          ? problems.join(" ")
          : "Every line is on the order at the ordered price.",
  };
}

/* ── queue tabs ────────────────────────────────────────────────────── */

/**
 * - `unaudited`: not yet entered (no lines).
 * - `logged`: lines, not all of them posted yet (a fresh log is still a `draft`).
 * - `posted`: every unrejected line has reached the ledger, and a purchase-order
 *   document marked reviewed (it has no lines, so nothing is left to post).
 */
export const reviewQueueTabs = ["unaudited", "logged", "posted"] as const;
export type ReviewQueueTab = (typeof reviewQueueTabs)[number];

/** Posted only grows and nobody works through it, so it is paged. */
export const REVIEW_POSTED_PAGE_SIZE = 50;

/**
 * Which tab an item belongs to. Derived from the states already on the wire, so
 * there is no stored state to disagree with. `readManualInbox` filters in SQL by
 * the same rule (`queueTabSql`).
 */
export function queueTabFor(
  item: Pick<InboxItem, "entryState" | "reviewState" | "lineCount">,
): ReviewQueueTab {
  if (item.reviewState === "posted") return "posted";
  if (item.lineCount === 0 && item.reviewState === "reviewed") return "posted";
  // Lines that are not all posted: logged. A freshly logged invoice is still a
  // `draft` until somebody confirms it, so the review state alone cannot say it.
  if (item.lineCount > 0) return "logged";
  return "unaudited";
}

export type ReviewQueueCounts = Record<ReviewQueueTab, number>;
